import type { ICommentAttachment, ICommentLink, WorkOrderTransitionBlockedReason } from '@atlas/types'
import { ReviewVerdict, WorkOrderChangeOrigin, type WorkOrderSyncAction } from '@atlas/types'
import { type EventDTO, HIDDEN_WORK_ORDER_EVENT_TYPES } from '@/services/activity.service'
import type { CommentDTO } from '@/services/comment.service'
import { SYNC_ACTION_LABEL } from './work-order-history'

/**
 * A single row in the work-order detail timeline (D10): comments and audit
 * events merged into one chronological stream. Comments are user-authored and
 * editable; activity entries are system-generated, read-only descriptions of
 * lifecycle events (create / edit / transition / upload / archive).
 */
export type TimelineEntry =
	| {
			kind: 'comment'
			id: string
			at: string
			/** Null for a comment from an external source, which has no ATLAS author. */
			authorId: string | null
			content: string
			/** Set on a comment from an external source: read-only, authored outside ATLAS. */
			external?: ExternalCommentRef
			/** How deep in its thread the comment sits; 0 (or absent) for a top-level comment. */
			depth?: number
			attachments?: ICommentAttachment[]
			links?: ICommentLink[]
	  }
	// `event` carries the entry through to its expanded detail: the label answers
	// what happened, the payload answers what changed.
	| { kind: 'activity'; id: string; at: string; actorId?: string; label: string; event: EventDTO }

/** Where an external comment came from, and the note it answers. */
export interface ExternalCommentRef {
	source: string
	externalId: string
	parentExternalId: string | null
	/** The author's name in the external system; there is no ATLAS user behind it. */
	authorName: string | null
}

/**
 * A blocked transition's structured reason, in the reader's words. "Blocked"
 * without a reason is not evidence — it is the reason that tells a reviewer
 * whether the block was configuration, permission, or an unmet gate.
 */
const BLOCK_REASONS: Record<string, string> = {
	...({
		invalid_workflow: 'its workflow configuration is invalid',
		unavailable_status: 'the target status is not available',
		illegal_transition: 'no transition exists for that move',
		backward_forbidden: 'moving backward is not permitted',
		review_forbidden: 'only a reviewer can move work out of review',
		guard_unmet: 'a required gate was not satisfied',
		deliverables_unreviewed: 'a final visual was not marked reviewed',
	} satisfies Record<WorkOrderTransitionBlockedReason, string>),
	// No longer emitted, but events written before substatus was retired carry it.
	invalid_substatus: 'the substatus is not valid for that status',
}

type Payload = Record<string, unknown>

const itemLabelOf = (d: Payload, fallback = 'an item') => (typeof d.itemLabel === 'string' ? d.itemLabel : fallback)
const countOf = (value: unknown) => (Array.isArray(value) ? value.length : 0)
const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

function describeStatusChange(d: Payload): string {
	// `substatus` is no longer emitted, but events written before it was
	// retired still carry it. This feed renders an immutable audit record, so
	// an old entry keeps reporting the substate it actually recorded rather
	// than being retroactively flattened to its status alone.
	const to = d.to as { status?: string; substatus?: string | null } | undefined
	const status = to?.substatus ? `${to.status} · ${to.substatus}` : (to?.status ?? 'a new status')
	const reopened = countOf(d.reopenedChangeRequestItems)
	return reopened
		? `moved status to ${status}, reopening ${plural(reopened, 'change-request item')}`
		: `moved status to ${status}`
}

/** Per-type label, reading off the payload where the type needs it. */
const LABELS: Record<string, (d: Payload) => string> = {
	'work_order.updated': (d) =>
		d.origin === WorkOrderChangeOrigin.WORKFRONT ? 'updated the work order from Workfront' : 'edited the work order',
	'work_order.sync_applied': (d) =>
		`applied a Workfront version: ${SYNC_ACTION_LABEL[d.action as WorkOrderSyncAction] ?? 'applied a version'}`,
	'work_order.file_attached': () => 'attached a file',
	'work_order.archived': () => 'archived the work order',
	'work_order.created': (d) =>
		typeof d.source === 'string' && d.source !== 'api'
			? `created this work order (via ${d.source})`
			: 'created this work order',
	'work_order.status_changed': describeStatusChange,
	'work_order.transition_blocked': (d) => {
		const reason = typeof d.reason === 'string' ? BLOCK_REASONS[d.reason] : undefined
		return reason ? `was blocked from moving status — ${reason}` : 'was blocked from moving status'
	},
	'work_order.validation_asset_requested': describeValidationAsset,
	'work_order.generation_context_saved': describeGenerationContext,
	'work_order.checklist_item_completed': (d) => `completed the checklist item "${itemLabelOf(d, 'a checklist item')}"`,
	'work_order.change_request_raised': (d) => {
		const count = countOf(d.itemLabels)
		return count ? `raised a change request (${plural(count, 'item')})` : 'raised a change request'
	},
	'work_order.change_request_item_verified': (d) => `verified the change-request item "${itemLabelOf(d)}"`,
	'work_order.change_request_item_unverified': (d) =>
		`rejected the fix for the change-request item "${itemLabelOf(d)}"`,
	'work_order.change_request_item_removed': (d) => `removed the change-request item "${itemLabelOf(d)}"`,
	'work_order.change_request_deleted': () => 'deleted a change request',
	'work_order.deliverable_reviewed': (d) =>
		d.verdict === ReviewVerdict.CLEARED
			? 'cleared the review mark on a final visual'
			: 'marked a final visual reviewed',
	'image.generation_requested': (d) =>
		`requested an image generation${typeof d.targetFormat === 'string' ? ` (${d.targetFormat})` : ''}`,
}

/** Human label for a work-order audit event (falls back to a humanized type). */
export function describeEvent(event: EventDTO): string {
	const label = LABELS[event.type]
	return label ? label(event.data ?? {}) : humanizeEventType(event.type)
}

/**
 * Fallback label for an event type with no dedicated description, e.g.
 * `file.uploaded` → "file uploaded". Never empty and never the raw dotted
 * identifier: an audit line a reader cannot parse is the same as a missing one.
 */
function humanizeEventType(type: string): string {
	const humanized = type.replace(/[._]+/g, ' ').trim()
	return humanized || 'recorded an event'
}

/**
 * The comment thread as timeline entries, oldest first — a chat/thread reads
 * top-to-bottom. Stable for equal timestamps so same-instant rows keep their
 * source order.
 *
 * Audit events are NOT merged in: they are paged and newest-first, and sorting
 * both into one array would destroy the ordering each side needs. The Activity
 * tab builds its own entries from a page of the audit log.
 */
export function buildTimeline(comments: CommentDTO[]): TimelineEntry[] {
	const entries = comments.map(
		(c): CommentTimelineEntry => ({
			kind: 'comment',
			id: c.id,
			at: c.createdAt,
			authorId: c.authorId,
			content: c.content,
			...(externalOf(c) ? { external: externalOf(c) } : {}),
			attachments: c.metadata?.attachments,
			links: c.metadata?.links,
		}),
	)
	return threaded(entries.sort((a, b) => a.at.localeCompare(b.at)))
}

type CommentTimelineEntry = Extract<TimelineEntry, { kind: 'comment' }>
export type ActivityTimelineEntry = Extract<TimelineEntry, { kind: 'activity' }>

/** The external origin of a comment, read from its metadata; undefined for an ATLAS comment. */
export function externalOf(comment: Pick<CommentDTO, 'metadata'>): ExternalCommentRef | undefined {
	const meta = comment.metadata
	if (meta?.readOnly !== true || typeof meta.externalId !== 'string') return undefined
	const text = (value: unknown) => (typeof value === 'string' ? value : null)
	return {
		source: text(meta.source) ?? 'external',
		externalId: meta.externalId,
		parentExternalId: text(meta.parentExternalId),
		authorName: text(meta.authorName),
	}
}

/**
 * Replies placed under the note they answer, each thread oldest first. A reply whose parent
 * is not in the list (removed upstream, or not synced yet) stays at the top level in time
 * order rather than vanishing. Input must already be oldest first.
 */
function threaded(entries: CommentTimelineEntry[]): CommentTimelineEntry[] {
	const byExternalId = new Map(entries.flatMap((e) => (e.external ? [[e.external.externalId, e] as const] : [])))
	const parentOf = (e: CommentTimelineEntry) => {
		const parentId = e.external?.parentExternalId
		const parent = parentId ? byExternalId.get(parentId) : undefined
		return parent && parent !== e ? parent : undefined
	}
	const repliesTo = new Map<CommentTimelineEntry, CommentTimelineEntry[]>()
	for (const e of entries) {
		const parent = parentOf(e)
		if (parent) repliesTo.set(parent, [...(repliesTo.get(parent) ?? []), e])
	}
	const out: CommentTimelineEntry[] = []
	const placed = new Set<CommentTimelineEntry>()
	const place = (e: CommentTimelineEntry, depth: number) => {
		if (placed.has(e)) return
		placed.add(e)
		out.push(depth > 0 ? { ...e, depth } : e)
		for (const reply of repliesTo.get(e) ?? []) place(reply, depth + 1)
	}
	for (const e of entries) if (!parentOf(e)) place(e, 0)
	// A reply cycle has no root; its members still belong on the page.
	for (const e of entries) place(e, 0)
	return out
}

/**
 * One page of audit events as timeline entries, in the order the server returned them (newest
 * first). The server already leaves the hidden types out; they are dropped here too, so a page
 * from a server that does not yet honour the exclusion still never shows one.
 */
export function buildActivityEntries(events: EventDTO[]): ActivityTimelineEntry[] {
	return events
		.filter((e) => !(HIDDEN_WORK_ORDER_EVENT_TYPES as readonly string[]).includes(e.type))
		.map(
			(e): ActivityTimelineEntry => ({
				kind: 'activity',
				id: e.eventId,
				at: e.timestamp,
				actorId: e.userId,
				label: describeEvent(e),
				event: e,
			}),
		)
}

/**
 * The generation-context line, naming the variables that were set.
 *
 * The KEYS rather than the values: the values live on the row this event points at,
 * and what a reader wants from a trail line is which knobs someone turned.
 */
function describeGenerationContext(data: Record<string, unknown>): string {
	const keys = Array.isArray(data.variableKeys) ? (data.variableKeys as string[]) : []
	return keys.length > 0 ? `set the generation context (${keys.join(', ')})` : 'set the generation context'
}

/** The validation-asset line, naming the asset type when the payload carries one. */
function describeValidationAsset(d: Record<string, unknown>): string {
	const assetType = typeof d.assetType === 'string' ? ` (${d.assetType})` : ''
	return `requested a validation asset${assetType}`
}
